import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Checkbox } from "@mui/material";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";
import workspaceService from "api/workspaceService";
import { ENotificationType, notification } from "utils/notification";

interface Props {
  workspaceId?: string;
  onBack: () => void;
  onSaved: () => void;
}

const elementOptions = {
  style: {
    base: {
      fontSize: "14px",
      color: "#1c1b1f",
      fontFamily: "Rubik, sans-serif",
      "::placeholder": { color: "#a29ea3" },
    },
    invalid: { color: "#ba1a1a" },
  },
};

const CardForm = ({
  clientSecret,
  workspaceId,
  onBack,
  onSaved,
}: { clientSecret: string } & Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || submitting || !agree) return;
    setSubmitting(true);

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setSubmitting(false);
      return;
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardNumber },
    });

    if (result.error || !result.setupIntent?.payment_method) {
      notification({
        type: ENotificationType.ERROR,
        title: "Karta płatnicza",
        text: result.error?.message ?? "Nie udało się zweryfikować karty.",
      });
      setSubmitting(false);
      return;
    }

    const pm = result.setupIntent.payment_method;
    const paymentMethodId = typeof pm === "string" ? pm : pm.id;

    try {
      await workspaceService.attachPaymentMethod({
        WorkspaceId: workspaceId?.length ? workspaceId : null,
        PaymentMethodId: paymentMethodId,
      });
      notification({
        type: ENotificationType.SUCCESS,
        title: "Karta płatnicza",
        text: "Dodano kartę płatniczą.",
      });
      onSaved();
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: "Karta płatnicza",
        text: "Nie udało się zapisać karty.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.field}>
        <div className={styles.fieldLabel}>Numer karty</div>
        <div className={styles.element}>
          <CardNumberElement options={elementOptions} />
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Termin ważności</div>
          <div className={styles.element}>
            <CardExpiryElement options={elementOptions} />
          </div>
        </div>
        <div className={styles.field}>
          <div className={styles.fieldLabel}>CVC</div>
          <div className={styles.element}>
            <CardCvcElement options={elementOptions} />
          </div>
        </div>
      </div>

      <div className={styles.agree}>
        <Checkbox
          size="small"
          color="primary"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          sx={{ padding: 0 }}
        />
        <span>
          Podając dane karty wyrażasz zgodę na cykliczne obciążanie jej zgodnie z
          regulaminem.
        </span>
      </div>

      <div className={styles.actions}>
        <HollowButton text="Anuluj" onClick={onBack} />
        <Button
          text="Dodaj"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!agree}
        />
      </div>
    </>
  );
};

const EditCardPanel = ({ workspaceId, onBack, onSaved }: Props) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    workspaceService
      .createSetupIntent(workspaceId?.length ? workspaceId : undefined)
      .then((r) => {
        if (!active) return;
        setClientSecret(r.data.ClientSecret);
        setStripePromise(loadStripe(r.data.PublishableKey));
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [workspaceId]);

  return (
    <div className={styles.panel}>
      <div className={styles.breadcrumb}>
        <FontAwesomeIcon
          icon={faArrowLeft}
          className={styles.back}
          onClick={onBack}
        />
        <span className={styles.muted}>Płatności</span>
        <FontAwesomeIcon icon={faChevronLeft} className={styles.sep} />
        <span>Karta płatnicza</span>
      </div>

      <div className={styles.hint}>
        Dane karty są przetwarzane bezpiecznie przez Stripe — nie trafiają na
        nasze serwery.
      </div>

      {loading && <Loading withoutText />}
      {error && (
        <div className={styles.error}>
          Nie udało się zainicjować płatności. Spróbuj ponownie.
        </div>
      )}
      {!loading && !error && clientSecret && stripePromise && (
        <Elements stripe={stripePromise}>
          <CardForm
            clientSecret={clientSecret}
            workspaceId={workspaceId}
            onBack={onBack}
            onSaved={onSaved}
          />
        </Elements>
      )}
    </div>
  );
};

export default EditCardPanel;
